import React, { useState } from 'react';
import {
  Box, Button, IconButton, Typography, Popover, TextField, Chip, Autocomplete,
  Tooltip, alpha,
} from '@mui/material';
import { PlayArrow, Stop, Delete, FiberManualRecord } from '@mui/icons-material';
import { useTimeTracking } from '../context_providers/TimeTrackingContext';
import { useProjects } from '../context_providers/ProjectContext';
import { Project } from '../../types';

const formatTime = (totalSeconds: number): string => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

const TimerWidget: React.FC = () => {
  const { activeTimer, startTimer, stopTimer, discardTimer } = useTimeTracking();
  const { projects } = useProjects();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isRunning = Boolean(activeTimer);
  const popoverOpen = Boolean(anchorEl);

  const handleStartClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setSelectedProject(null);
    setDescription('');
  };

  const handleStartTimer = async () => {
    setIsSubmitting(true);
    try {
      await startTimer({
        projectId: selectedProject?.id,
        description: description || undefined,
      });
      handlePopoverClose();
    } catch {
      // Error handled by context
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStop = async () => {
    try {
      await stopTimer();
    } catch {
      // Error handled by context
    }
  };

  const handleDiscard = async () => {
    try {
      await discardTimer();
    } catch {
      // Error handled by context
    }
  };

  const runningProject = isRunning && activeTimer?.entry?.projectId
    ? projects.find(p => p.id === activeTimer.entry.projectId)
    : null;

  if (isRunning && activeTimer) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mr: 2,
          px: 2,
          py: 0.5,
          borderRadius: 2,
          bgcolor: (theme) => alpha(theme.palette.success.main, 0.1),
          border: (theme) => `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
        }}
      >
        <FiberManualRecord
          sx={{
            fontSize: 12,
            color: 'success.main',
            animation: 'pulse 1.5s ease-in-out infinite',
            '@keyframes pulse': {
              '0%': { opacity: 1 },
              '50%': { opacity: 0.3 },
              '100%': { opacity: 1 },
            },
          }}
        />

        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            fontWeight: 700,
            fontSize: '0.95rem',
            letterSpacing: '0.05em',
            color: 'text.primary',
            minWidth: 72,
          }}
        >
          {formatTime(activeTimer.elapsedSeconds)}
        </Typography>

        {runningProject && (
          <Chip
            label={runningProject.name}
            size="small"
            sx={{
              maxWidth: 140,
              bgcolor: runningProject.color || 'primary.main',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 22,
            }}
          />
        )}

        <Tooltip title="Stop timer">
          <IconButton
            size="small"
            onClick={handleStop}
            sx={{
              bgcolor: 'error.main',
              color: '#fff',
              width: 28,
              height: 28,
              '&:hover': { bgcolor: 'error.dark' },
            }}
          >
            <Stop sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Discard timer">
          <IconButton
            size="small"
            onClick={handleDiscard}
            sx={{
              color: 'text.secondary',
              width: 28,
              height: 28,
              '&:hover': { color: 'error.main' },
            }}
          >
            <Delete sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <>
      <Button
        variant="contained"
        color="success"
        size="small"
        startIcon={<PlayArrow />}
        onClick={handleStartClick}
        sx={{
          mr: 2,
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 2,
          px: 2,
        }}
      >
        Start
      </Button>

      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { p: 2.5, width: 320, mt: 1, borderRadius: 2 },
          },
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
          Start Timer
        </Typography>

        <Autocomplete
          options={projects.filter(p => p.status === 'active')}
          getOptionLabel={(option) => option.name}
          value={selectedProject}
          onChange={(_, value) => setSelectedProject(value)}
          renderOption={(props, option) => (
            <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: option.color || 'primary.main',
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2" noWrap>{option.name}</Typography>
              {option.code && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  {option.code}
                </Typography>
              )}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Project"
              size="small"
              placeholder="Select a project..."
            />
          )}
          size="small"
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          size="small"
          label="Description"
          placeholder="What are you working on?"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          minRows={2}
          maxRows={3}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleStartTimer();
            }
          }}
          sx={{ mb: 2 }}
        />

        <Button
          fullWidth
          variant="contained"
          color="success"
          startIcon={<PlayArrow />}
          onClick={handleStartTimer}
          disabled={isSubmitting}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {isSubmitting ? 'Starting...' : 'Start Timer'}
        </Button>
      </Popover>
    </>
  );
};

export default TimerWidget;
